import adminApi from './adminApi';
import { notificationService } from './notificationService';
import { initializeWebSocket, autoConnectWebSocket } from './webSocketService';
import { API_CONFIG, getAdminUrl, ENDPOINTS } from '../config/apiConfig';

// Re-export API_CONFIG for backward compatibility
export { API_CONFIG } from '../config/apiConfig';

// Initialize API services
export function initializeAPI(): void {
  // Configure notification service
  notificationService.configure({
    placement: 'topRight',
    duration: 4.5,
  });

  // Test API connectivity
  testAPIConnectivity();

  // Initialize WebSocket if authenticated
  const token = localStorage.getItem('adminToken');
  if (token) {
    autoConnectWebSocket();
  }

  // Set up global error handlers
  setupGlobalErrorHandlers();
}

// Test API connectivity
async function testAPIConnectivity(): Promise<void> {
  try {
    // Simple health check using axios directly (no auth needed)
    const response = await fetch(getAdminUrl(ENDPOINTS.admin.health));
    if (response.ok) {
      console.log('✅ Admin API connectivity test passed');
    } else {
      throw new Error(`Health check failed with status: ${response.status}`);
    }
  } catch (error) {
    console.warn('⚠️ Admin API connectivity test failed, mock mode will be used:', error);
    
    if (API_CONFIG.debugAPI) {
      notificationService.warning({
        title: 'API Connection Warning',
        description: 'Unable to connect to the admin API server. Using mock data for development.',
        duration: 6,
      });
    }
  }
}

// Setup global error handlers
function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Prevent the default browser behavior
    event.preventDefault();
    
    // Show user-friendly error message
    notificationService.error({
      title: 'Unexpected Error',
      description: 'An unexpected error occurred. Please try refreshing the page.',
    });
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    // Don't show notifications for script loading errors
    if (event.filename && event.filename.includes('.js')) {
      return;
    }
    
    notificationService.error({
      title: 'Application Error',
      description: 'An error occurred in the application. Please refresh the page if problems persist.',
    });
  });
}

// Authentication helpers
// Helper functions for authentication
export function setAuthenticationToken(token: string): void {
  // This is handled by the adminApi interceptor
  localStorage.setItem('adminToken', token);
}

export function clearAuthenticationToken(): void {
  // This is handled by the adminApi interceptor
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminUser');
}

// API health check
// Validate API connection
export async function validateAPIConnection(): Promise<boolean> {
  try {
    await adminApi.get('/health');
    return true;
  } catch (error) {
    console.error('API validation failed:', error);
    return false;
  }
}

// Development utilities
export const DevUtils = {
  // Clear all caches
  clearCaches: () => {
    localStorage.removeItem('cache_data');
    console.log('✅ Caches cleared');
  },
  
  // Test WebSocket connection
  testWebSocket: () => {
    const ws = initializeWebSocket({
      url: API_CONFIG.wsURL,
      debug: true,
    });
    
    ws.connect()
      .then(() => {
        console.log('✅ WebSocket connection test passed');
        ws.send('test', { message: 'Hello from admin panel!' });
      })
      .catch((error) => {
        console.error('❌ WebSocket connection test failed:', error);
      });
  },
  
  // Show API configuration
  showConfig: () => {
    console.table(API_CONFIG);
  },
};

// Export for global access in development
if (import.meta.env.NODE_ENV === 'development') {
  (window as any).DevUtils = DevUtils;
}
